import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [loginError, setLoginError] = useState(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required')
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoginError(null);
      // Call the backend API to authenticate
      await login(values);

      // Successful login - redirect to dashboard
      navigate('/dashboard');
    } catch (error) {
      // Failed login
      setLoginError('Invalid credentials. Only authorized government personnel can access this system.');
      console.error('Login error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <h1 className="text-3xl font-bold text-primary mb-8 text-center">Government Staff Login</h1>

      <div className="bg-white p-6 rounded-lg shadow-md">
        {loginError && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded mb-4">
            {loginError}
          </div>
        )}

        <Formik
          initialValues={{ email: '', password: '' }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <label htmlFor="email" className="form-label">Email Address</label>
                <Field name="email" type="email" className="form-input" />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label htmlFor="password" className="form-label">Password</label>
                <Field name="password" type="password" className="form-input" />
                <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full"
                >
                  {isSubmitting ? 'Logging in...' : 'Log In'}
                </button>
              </div>
            </Form>
          )}
        </Formik>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            This login is restricted to authorized government personnel only.
            Unauthorized access attempts may be subject to legal action.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
